import React, { createContext, useContext, useState, useEffect } from 'react';
import API_BASE_URL from '../../config';

export interface InviteUser {
    id: string;
    name: string;
    email: string;
    phone?: string;
    profilePhoto?: string;
    isPremium: boolean;
    premiumPlan?: string;
    totalInvitesSent: number;
    totalFreeInvitesSent: number;
    totalMediaInvitesSent?: number;
    maxInvitations: number;
    remainingInvites: number;
    whatsappStatus?: string;
    whatsappPhoneNumber?: string;
    aiGenerationsUsed?: number;
    hasAiPlan?: boolean;
    aiRemaining?: number;
}

interface InviteAuthContextType {
    inviteUser: InviteUser | null;
    inviteToken: string | null;
    inviteLogin: (email: string, password: string) => Promise<void>;
    inviteRegister: (name: string, email: string, password: string, phone?: string) => Promise<void>;
    inviteGoogleLogin: (idToken: string) => Promise<void>;
    inviteLogout: () => void;
    inviteUpgrade: (plan: string) => Promise<void>;
    upgradeAiPlan: () => Promise<void>;
    refreshProfile: () => Promise<void>;
    createCashfreeOrder: (plan: string, isSimulator?: boolean) => Promise<{
        orderId: string;
        paymentSessionId: string;
        orderAmount: number;
        environment: 'sandbox' | 'production';
        isMock: boolean;
    }>;
    verifyCashfreePayment: (orderId: string, plan: string, isSimulator?: boolean) => Promise<any>;
    loading: boolean;
    error: string | null;
}

const InviteAuthContext = createContext<InviteAuthContextType | undefined>(undefined);

const mapUserData = (u: any): InviteUser => {
    const max = u.maxInvitations || 10;
    const sent = u.totalInvitesSent || 0;
    return {
        id: u._id || u.id,
        name: u.name,
        email: u.email,
        phone: u.phone,
        profilePhoto: u.profilePhoto,
        isPremium: Boolean(u.isPremium),
        premiumPlan: u.premiumPlan,
        totalInvitesSent: sent,
        totalFreeInvitesSent: u.totalFreeInvitesSent || 0,
        totalMediaInvitesSent: u.totalMediaInvitesSent || 0,
        maxInvitations: max,
        remainingInvites: typeof u.remainingInvites === 'number' ? u.remainingInvites : Math.max(0, max - sent),
        whatsappStatus: u.whatsappStatus,
        whatsappPhoneNumber: u.whatsappPhoneNumber,
        aiGenerationsUsed: u.aiGenerationsUsed || 0,
        hasAiPlan: Boolean(u.hasAiPlan),
        aiRemaining: u.hasAiPlan ? 999999 : (typeof u.aiRemaining === 'number' ? u.aiRemaining : Math.max(0, 5 - (u.aiGenerationsUsed || 0))),
    };
};

export const InviteAuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [inviteUser, setInviteUser] = useState<InviteUser | null>(null);
    const [inviteToken, setInviteToken] = useState<string | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const checkAuth = async () => {
            const token = localStorage.getItem('invite_accessToken');
            if (!token) {
                setLoading(false);
                return;
            }

            try {
                setInviteToken(token);
                const response = await fetch(`${API_BASE_URL}/api/invite/auth/profile`, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                });

                if (response.ok) {
                    const data = await response.json();
                    setInviteUser(mapUserData(data.user));
                } else {
                    // Token expired or invalid
                    localStorage.removeItem('invite_accessToken');
                    setInviteToken(null);
                    setInviteUser(null);
                }
            } catch (err: any) {
                console.error('[InviteAuthContext] Profile fetch error:', err.message);
            } finally {
                setLoading(false);
            }
        };

        checkAuth();
    }, []);

    const inviteLogin = async (email: string, password: string) => {
        setError(null);
        try {
            const response = await fetch(`${API_BASE_URL}/api/invite/auth/login`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email, password }),
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.message || 'Login failed');
            }

            localStorage.setItem('invite_accessToken', data.token);
            setInviteToken(data.token);
            setInviteUser(mapUserData(data.user));
        } catch (err: any) {
            setError(err.message);
            throw err;
        }
    };

    const inviteRegister = async (name: string, email: string, password: string, phone?: string) => {
        setError(null);
        try {
            const response = await fetch(`${API_BASE_URL}/api/invite/auth/register`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ name, email, password, phone }),
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.message || 'Registration failed');
            }

            localStorage.setItem('invite_accessToken', data.token);
            setInviteToken(data.token);
            setInviteUser(mapUserData(data.user));
        } catch (err: any) {
            setError(err.message);
            throw err;
        }
    };

    const inviteGoogleLogin = async (idToken: string) => {
        setError(null);
        try {
            const response = await fetch(`${API_BASE_URL}/api/invite/auth/google`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ idToken }),
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.message || 'Google Login failed');
            }

            localStorage.setItem('invite_accessToken', data.token);
            setInviteToken(data.token);
            setInviteUser(mapUserData(data.user));
        } catch (err: any) {
            setError(err.message);
            throw err;
        }
    };

    const inviteLogout = () => {
        localStorage.removeItem('invite_accessToken');
        setInviteToken(null);
        setInviteUser(null);
        setError(null);
    };

    const refreshProfile = async () => {
        const token = localStorage.getItem('invite_accessToken') || inviteToken;
        if (!token) return;
        try {
            const res = await fetch(`${API_BASE_URL}/api/invite/auth/profile`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (res.ok) {
                const data = await res.json();
                setInviteUser(mapUserData(data.user));
            }
        } catch (e) {
            console.warn('[InviteAuthContext] refreshProfile error:', e);
        }
    };

    const upgradeAiPlan = async () => {
        if (!inviteToken) throw new Error('Please login to activate the Nano Banana AI Plan.');
        const res = await fetch(`${API_BASE_URL}/api/invite/ai/upgrade-plan`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${inviteToken}`,
            },
        });
        const data = await res.json();
        if (res.ok && data.user) {
            setInviteUser(mapUserData(data.user));
        } else {
            throw new Error(data.message || 'Failed to activate AI Plan');
        }
    };

    const inviteUpgrade = async (plan: string) => {
        if (!inviteToken) return;
        try {
            const response = await fetch(`${API_BASE_URL}/api/invite/auth/upgrade`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${inviteToken}`,
                },
                body: JSON.stringify({ plan }),
            });
            const data = await response.json();
            if (response.ok) {
                setInviteUser(mapUserData(data.user));
            } else {
                throw new Error(data.message || 'Upgrade failed');
            }
        } catch (err: any) {
            setError(err.message);
            throw err;
        }
    };

    const createCashfreeOrder = async (plan: string, isSimulator?: boolean): Promise<{
        orderId: string;
        paymentSessionId: string;
        orderAmount: number;
        environment: 'sandbox' | 'production';
        isMock: boolean;
    }> => {
        if (!inviteToken) {
            throw new Error('Please login to proceed with payment.');
        }

        const returnUrl = `${window.location.origin}/invite/upgrade?order_id={order_id}&plan=${plan}`;
        const response = await fetch(`${API_BASE_URL}/api/invite/payment/cashfree/create-order`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${inviteToken}`,
            },
            body: JSON.stringify({ plan, isSimulator, returnUrl }),
        });

        const data = await response.json();
        if (!response.ok || !data.success) {
            throw new Error(data.message || 'Failed to initiate Cashfree order.');
        }

        return data;
    };

    const verifyCashfreePayment = async (orderId: string, plan: string, isSimulator?: boolean) => {
        if (!inviteToken) {
            throw new Error('Please login to verify payment.');
        }

        const response = await fetch(`${API_BASE_URL}/api/invite/payment/cashfree/verify`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${inviteToken}`,
            },
            body: JSON.stringify({ orderId, plan, isSimulator }),
        });

        const data = await response.json();
        if (!response.ok || !data.success) {
            throw new Error(data.message || 'Payment verification failed.');
        }

        if (data.user) {
            setInviteUser(mapUserData(data.user));
        }

        return data;
    };

    return (
        <InviteAuthContext.Provider
            value={{
                inviteUser,
                inviteToken,
                inviteLogin,
                inviteRegister,
                inviteGoogleLogin,
                inviteLogout,
                inviteUpgrade,
                upgradeAiPlan,
                refreshProfile,
                createCashfreeOrder,
                verifyCashfreePayment,
                loading,
                error,
            }}
        >
            {children}
        </InviteAuthContext.Provider>
    );
};

export const useInviteAuth = () => {
    const context = useContext(InviteAuthContext);
    if (context === undefined) {
        throw new Error('useInviteAuth must be used within an InviteAuthProvider');
    }
    return context;
};
